/**
 * attribution.js
 *
 * Captures campaign parameters on landing, persists them in first-party cookies,
 * and decorates cross-origin iframe URLs so form vendors receive attribution
 * even when the user navigates away from the landing page before submitting.
 *
 * Load this in <head>, before any iframe is injected.
 *
 *   Attribution.get()                 -> { first: {...}, last: {...} }
 *   Attribution.params()              -> flat object ready for a query string
 *   Attribution.decorate(url)         -> url with attribution params appended
 *   Attribution.mount()               -> sets src on [data-attr-src] iframes
 */
(function (window, document) {
  'use strict';

  var CONFIG = {
    firstTouchCookie: 'attr_first',
    lastTouchCookie: 'attr_last',
    days: 90,
    cookieDomain: null,   // e.g. '.example.com'; null = derive from hostname
    // Hostnames that should NOT count as an external referrer (own domains,
    // payment gateways, SSO providers, the form vendor itself).
    internalHosts: []
  };

  var CAMPAIGN_KEYS = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_term',
    'utm_content', 'utm_id', 'utm_source_platform'
  ];

  var CLICK_ID_KEYS = [
    'gclid', 'gbraid', 'wbraid', 'dclid', 'gad_source',
    'msclkid', 'fbclid', 'ttclid', 'twclid', 'li_fat_id',
    'irclickid', 'epik', 'sccid'
  ];

  var TRACKED = CAMPAIGN_KEYS.concat(CLICK_ID_KEYS);

  // ---------------------------------------------------------------- cookies

  function domainAttr() {
    if (CONFIG.cookieDomain) return '; domain=' + CONFIG.cookieDomain;
    // Naive eTLD+1. Override CONFIG.cookieDomain for multi-part TLDs
    // (co.uk, com.au) or if you don't need subdomain sharing.
    var parts = window.location.hostname.split('.');
    if (parts.length < 3) return '';
    return '; domain=.' + parts.slice(-2).join('.');
  }

  function readCookie(name) {
    var match = document.cookie.match(
      new RegExp('(?:^|; )' + name.replace(/([.*+?^${}()|[\]\\])/g, '\\$1') + '=([^;]*)')
    );
    if (!match) return null;
    try {
      return JSON.parse(decodeURIComponent(match[1]));
    } catch (e) {
      return null;
    }
  }

  function writeCookie(name, value) {
    var expires = new Date(Date.now() + CONFIG.days * 864e5).toUTCString();
    document.cookie = name + '=' + encodeURIComponent(JSON.stringify(value)) +
      '; expires=' + expires +
      '; path=/' + domainAttr() +
      '; SameSite=Lax' +
      (window.location.protocol === 'https:' ? '; Secure' : '');
  }

  // ---------------------------------------------------------------- capture

  function urlParams() {
    var out = {};
    var query = window.location.search.replace(/^\?/, '');
    if (!query) return out;
    query.split('&').forEach(function (pair) {
      if (!pair) return;
      var idx = pair.indexOf('=');
      var key = decodeURIComponent(idx < 0 ? pair : pair.slice(0, idx));
      var val = idx < 0 ? '' : decodeURIComponent(pair.slice(idx + 1).replace(/\+/g, ' '));
      if (TRACKED.indexOf(key) !== -1 && val) out[key] = val;
    });
    return out;
  }

  function referrerHost() {
    if (!document.referrer) return null;
    try {
      var host = new URL(document.referrer).hostname;
      var internal = CONFIG.internalHosts.concat([window.location.hostname]);
      for (var i = 0; i < internal.length; i++) {
        // match host and its subdomains
        if (host === internal[i] || host.slice(-(internal[i].length + 1)) === '.' + internal[i]) {
          return null;
        }
      }
      return host;
    } catch (e) {
      return null;
    }
  }

  function capture() {
    var params = urlParams();
    var hasCampaign = Object.keys(params).length > 0;
    var refHost = referrerHost();

    // Direct or internal navigation with no campaign params: change nothing.
    // This is the whole point — an empty capture must never overwrite a good one.
    if (!hasCampaign && !refHost) return;

    var record = {
      ts: new Date().toISOString(),
      landing: window.location.pathname,
      referrer_host: refHost || null
    };
    TRACKED.forEach(function (key) {
      if (params[key]) record[key] = params[key];
    });

    // Fill in an inferred source for organic/referral so last-touch isn't blank.
    if (!record.utm_source && refHost) {
      record.utm_source = refHost;
      record.utm_medium = 'referral';
    }

    if (!readCookie(CONFIG.firstTouchCookie)) {
      writeCookie(CONFIG.firstTouchCookie, record);
    }
    writeCookie(CONFIG.lastTouchCookie, record);
  }

  // ---------------------------------------------------------------- read out

  function get() {
    return {
      first: readCookie(CONFIG.firstTouchCookie),
      last: readCookie(CONFIG.lastTouchCookie)
    };
  }

  /**
   * Flat params for the iframe query string.
   * Live URL params win over the cookie, so a fresh landing is always accurate.
   */
  function params() {
    var stored = get();
    var live = urlParams();
    var out = {};

    if (stored.last) {
      TRACKED.forEach(function (key) {
        if (stored.last[key]) out[key] = stored.last[key];
      });
      if (stored.last.referrer_host) out.attr_referrer = stored.last.referrer_host;
    }

    Object.keys(live).forEach(function (key) { out[key] = live[key]; });

    if (stored.first) {
      // Prefix first-touch so it doesn't collide with the last-touch fields.
      CAMPAIGN_KEYS.forEach(function (key) {
        if (stored.first[key]) out['first_' + key] = stored.first[key];
      });
      if (stored.first.ts) out.first_touch_at = stored.first.ts;
      if (stored.first.landing) out.first_landing_page = stored.first.landing;
    }

    return out;
  }

  function decorate(url) {
    var extra = params();
    var keys = Object.keys(extra);
    if (!keys.length) return url;

    var hashIdx = url.indexOf('#');
    var hash = hashIdx > -1 ? url.slice(hashIdx) : '';
    var base = hashIdx > -1 ? url.slice(0, hashIdx) : url;

    var existing = base.indexOf('?') > -1 ? base.slice(base.indexOf('?') + 1) : '';
    var present = {};
    existing.split('&').forEach(function (pair) {
      if (pair) present[decodeURIComponent(pair.split('=')[0])] = true;
    });

    var additions = keys
      .filter(function (key) { return !present[key]; })
      .map(function (key) {
        return encodeURIComponent(key) + '=' + encodeURIComponent(extra[key]);
      });

    if (!additions.length) return url;
    return base + (base.indexOf('?') > -1 ? '&' : '?') + additions.join('&') + hash;
  }

  /**
   * Markup:
   *   <iframe data-attr-src="https://forms.vendor.com/abc" ...></iframe>
   * The src is set here, after attribution is resolved, so the frame never
   * loads without it.
   */
  function mount(root) {
    var frames = (root || document).querySelectorAll('iframe[data-attr-src]');
    for (var i = 0; i < frames.length; i++) {
      var frame = frames[i];
      if (frame.getAttribute('data-attr-mounted')) continue;
      frame.setAttribute('data-attr-mounted', '1');
      frame.src = decorate(frame.getAttribute('data-attr-src'));
    }
  }

  function attributionUrl() {
  var a = window.Attribution.params();
  var keep = ['utm_source','utm_medium','utm_campaign','utm_term','utm_content','gclid','msclkid'];
  var qs = keep.filter(function (k) { return a[k]; })
               .map(function (k) { return k + '=' + encodeURIComponent(a[k]); })
               .join('&');
  return window.location.origin + window.location.pathname + (qs ? '?' + qs : '');
}

  capture();

  window.Attribution = {
    get: get,
    params: params,
    decorate: decorate,
    mount: mount,
    attributionUrl: attributionUrl,
    config: CONFIG
  };

//   if (document.readyState === 'loading') {
//     document.addEventListener('DOMContentLoaded', function () { mount(); });
//   } else {
//     mount();
//   }
})(window, document);